import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SelectedProduct, RentalFormData } from '../App';
import { ChevronDownIcon, CalendarIcon, PackageIcon, ArrowRightIcon } from 'lucide-react';
interface ProductReservePageProps {
  onNavigate: (page: string) => void;
  selectedProduct: SelectedProduct | null;
  onSubmitRentalForm: (data: RentalFormData) => void;
}
export function ProductReservePage({
  onNavigate,
  selectedProduct,
  onSubmitRentalForm
}: ProductReservePageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const rentalTypes = ['Residential', 'Hotel', 'Airbnb', 'Baltimore Ports', 'Seaports', 'Others'];
  const deliveryLocations = ['Washington DC', 'Maryland', 'Baltimore', 'Northern Virginia'];
  const equipmentOptions = ['Rollator Walker', 'Power Wheelchair', 'Knee Walker', 'Manual Wheelchair', '3 or 4 Wheel Scooter (Regular)', '3 or 4 Wheel Scooter (Heavy Duty)', 'Heavy Duty Manual Wheelchair', 'Baby Stroller'];
  const [formData, setFormData] = useState<RentalFormData>({
    rentalType: 'Residential',
    placeName: '',
    placeAddress: '',
    deliveryLocation: '',
    equipment: selectedProduct?.name || '',
    rentalDate: '',
    returnDate: '',
    weight: '',
    height: ''
  });
  const [agreeMinimum, setAgreeMinimum] = useState(false);
  const [agreeInsurance, setAgreeInsurance] = useState(false);
  if (!selectedProduct) {
    return <main className="flex-grow w-full bg-brand-gray min-h-screen flex flex-col" data-id="element-1160">
        <PageHeader title="Reserve Equipment" breadcrumb="Reserve" onNavigateHome={() => onNavigate('home')} data-id="element-1161" />
        <div className="flex-grow flex items-center justify-center p-8" data-id="element-1162">
          <div className="text-center bg-white p-12 rounded-3xl shadow-xl max-w-md w-full" data-id="element-1163">
            <div className="w-24 h-24 bg-brand-gray rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1164">
              <PackageIcon className="w-10 h-10 text-brand-navy/30" data-id="element-1165" />
            </div>
            <h2 className="text-2xl font-bold text-brand-navy mb-4" data-id="element-1166">
              No Product Selected
            </h2>
            <p className="text-brand-charcoal/60 mb-8" data-id="element-1167">
              Please select a product from our catalog to start a reservation.
            </p>
            <button onClick={() => onNavigate('home')} className="w-full py-4 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-lg" data-id="element-1168">
              View Products
            </button>
          </div>
        </div>
      </main>;
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreeMinimum || !agreeInsurance) {
      alert('Please agree to the minimum rental period and insurance terms.');
      return;
    }
    onSubmitRentalForm(formData);
  };
  const inputClass = 'w-full bg-white border border-gray-200 text-brand-navy placeholder-gray-400 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none';
  const labelClass = 'block text-brand-charcoal/80 text-sm font-semibold mb-2 uppercase tracking-wider';
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-1169">
      <PageHeader title="Reserve Equipment" breadcrumb="Reserve" onNavigateHome={() => onNavigate('home')} data-id="element-1170" />

      <section className="py-12 md:py-20" data-id="element-1171">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1172">
          <div className="flex flex-col lg:flex-row gap-8 items-start" data-id="element-1173">
            {/* Left Column: Product Summary */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full lg:w-[35%] lg:sticky lg:top-28" data-id="element-1174">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-1175">
                {/* Mobile: horizontal compact layout / Desktop: vertical */}
                <div className="flex flex-row lg:flex-col" data-id="element-1176">
                  <div className="bg-gray-50 p-4 lg:p-8 flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40 lg:w-full lg:h-auto lg:aspect-square shrink-0 relative border-b-0 lg:border-b border-r lg:border-r-0 border-gray-100" data-id="element-1177">
                    <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-contain mix-blend-multiply" data-id="element-1178" />
                  </div>
                  <div className="p-4 sm:p-6 lg:p-8 flex flex-col justify-center" data-id="element-1179">
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-navy mb-1 lg:mb-2" data-id="element-1180">
                      {selectedProduct.name}
                    </h2>
                    <div className="flex items-baseline mb-3 lg:mb-6" data-id="element-1181">
                      <span className="text-xs lg:text-sm text-brand-charcoal/60 mr-1" data-id="element-1182">
                        From
                      </span>
                      <span className="text-2xl lg:text-3xl font-extrabold text-brand-red" data-id="element-1183">
                        ${selectedProduct.price.toFixed(2)}
                      </span>
                      <span className="text-xs lg:text-sm text-brand-charcoal/60 ml-1" data-id="element-1184">
                        /day
                      </span>
                    </div>

                    {/* Benefits - hidden on mobile, shown on desktop */}
                    <div className="hidden lg:block space-y-4 pt-6 border-t border-gray-100" data-id="element-1185">
                      <div className="flex items-start gap-3" data-id="element-1186">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-1187">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1188">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1189" />
                          </svg>
                        </div>
                        <p className="text-sm text-brand-charcoal/70" data-id="element-1190">
                          Free delivery to your hotel, residence, or port
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1191">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-1192">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1193">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1194" />
                          </svg>
                        </div>
                        <p className="text-sm text-brand-charcoal/70" data-id="element-1195">
                          Sanitized and fully charged before delivery
                        </p>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-1196">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-1197">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1198">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1199" />
                          </svg>
                        </div>
                        <p className="text-sm text-brand-charcoal/70" data-id="element-1200">
                          24/7 customer support included
                        </p>
                      </div>
                    </div>

                    {/* Mobile benefits - compact inline */}
                    <div className="flex flex-wrap gap-2 lg:hidden" data-id="element-1201">
                      <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full font-medium" data-id="element-1202">
                        ✓ Free delivery
                      </span>
                      <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full font-medium" data-id="element-1203">
                        ✓ Sanitized
                      </span>
                      <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full font-medium" data-id="element-1204">
                        ✓ 24/7 support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Rental Form */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="w-full lg:w-[65%]" data-id="element-1205">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8 md:p-10" data-id="element-1206">
                <h3 className="text-2xl font-bold text-brand-navy mb-8 pb-4 border-b border-gray-100" data-id="element-1207">
                  Reservation Details
                </h3>

                <form onSubmit={handleSubmit} className="space-y-8" data-id="element-1208">
                  {/* Rental Type Pills */}
                  <div data-id="element-1209">
                    <label className={labelClass} data-id="element-1210">Rental Type</label>
                    <div className="flex flex-wrap gap-3" data-id="element-1211">
                      {rentalTypes.map(type => <button key={type} type="button" onClick={() => setFormData({
                      ...formData,
                      rentalType: type
                    })} className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${formData.rentalType === type ? 'bg-brand-red text-white shadow-md' : 'bg-brand-gray text-brand-charcoal/70 border border-gray-200 hover:bg-gray-100'}`} data-id="element-1212">
                          {type}
                        </button>)}
                    </div>
                  </div>

                  {/* Place Name & Address */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1213">
                    <div data-id="element-1214">
                      <label htmlFor="placeName" className={labelClass} data-id="element-1215">
                        Place Name
                      </label>
                      <input type="text" id="placeName" name="placeName" value={formData.placeName} onChange={handleInputChange} placeholder="Enter place name" required className={inputClass} data-id="element-1216" />
                    </div>
                    <div data-id="element-1217">
                      <label htmlFor="placeAddress" className={labelClass} data-id="element-1218">
                        Place Address
                      </label>
                      <input type="text" id="placeAddress" name="placeAddress" value={formData.placeAddress} onChange={handleInputChange} placeholder="Enter place address" required className={inputClass} data-id="element-1219" />
                    </div>
                  </div>

                  {/* Delivery Location & Equipment */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1220">
                    <div data-id="element-1221">
                      <label htmlFor="deliveryLocation" className={labelClass} data-id="element-1222">
                        Delivery Location
                      </label>
                      <div className="relative" data-id="element-1223">
                        <select id="deliveryLocation" name="deliveryLocation" value={formData.deliveryLocation} onChange={handleInputChange} required className={`${inputClass} appearance-none`} data-id="element-1224">
                          <option value="" disabled data-id="element-1225">
                            Select One
                          </option>
                          {deliveryLocations.map(loc => <option key={loc} value={loc} data-id="element-1226">
                              {loc}
                            </option>)}
                        </select>
                        <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1227" />
                      </div>
                    </div>
                    <div data-id="element-1228">
                      <label htmlFor="equipment" className={labelClass} data-id="element-1229">
                        Equipment
                      </label>
                      <div className="relative" data-id="element-1230">
                        <select id="equipment" name="equipment" value={formData.equipment} onChange={handleInputChange} required className={`${inputClass} appearance-none`} data-id="element-1231">
                          <option value="" disabled data-id="element-1232">
                            Select One
                          </option>
                          {equipmentOptions.map(eq => <option key={eq} value={eq} data-id="element-1233">
                              {eq}
                            </option>)}
                        </select>
                        <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1234" />
                      </div>
                    </div>
                  </div>

                  {/* Dates Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1235">
                    <div data-id="element-1236">
                      <label htmlFor="rentalDate" className={labelClass} data-id="element-1237">
                        Rental Date
                      </label>
                      <div className="relative" data-id="element-1238">
                        <input type="date" id="rentalDate" name="rentalDate" value={formData.rentalDate} onChange={handleInputChange} required className={`${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} data-id="element-1239" />
                        <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1240" />
                      </div>
                    </div>
                    <div data-id="element-1241">
                      <label htmlFor="returnDate" className={labelClass} data-id="element-1242">
                        Return Date
                      </label>
                      <div className="relative" data-id="element-1243">
                        <input type="date" id="returnDate" name="returnDate" value={formData.returnDate} onChange={handleInputChange} required className={`${inputClass} [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`} data-id="element-1244" />
                        <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1245" />
                      </div>
                    </div>
                  </div>

                  {/* Weight & Height Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1246">
                    <div data-id="element-1247">
                      <label htmlFor="weight" className={labelClass} data-id="element-1248">
                        Weight
                      </label>
                      <input type="text" id="weight" name="weight" value={formData.weight} onChange={handleInputChange} placeholder="Weight in Pounds" required className={inputClass} data-id="element-1249" />
                    </div>
                    <div data-id="element-1250">
                      <label htmlFor="height" className={labelClass} data-id="element-1251">
                        Height
                      </label>
                      <input type="text" id="height" name="height" value={formData.height} onChange={handleInputChange} placeholder="Height in ft/cm" required className={inputClass} data-id="element-1252" />
                    </div>
                  </div>

                  {/* Checkboxes */}
                  <div className="space-y-4 pt-4 border-t border-gray-100" data-id="element-1253">
                    <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1254">
                      <div className="relative flex items-center justify-center mt-0.5" data-id="element-1255">
                        <input type="checkbox" checked={agreeMinimum} onChange={e => setAgreeMinimum(e.target.checked)} required className="peer appearance-none w-5 h-5 border-2 border-gray-300 rounded bg-white checked:bg-brand-red checked:border-brand-red transition-all outline-none focus:ring-2 focus:ring-brand-red/50" data-id="element-1256" />
                        <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1257">
                          <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1258" />
                        </svg>
                      </div>
                      <span className="text-brand-charcoal/80 text-sm leading-relaxed group-hover:text-brand-navy transition-colors" data-id="element-1259">
                        3 days minimum for all rentals. You can call for more
                        information or details.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer group" data-id="element-1260">
                      <div className="relative flex items-center justify-center mt-0.5" data-id="element-1261">
                        <input type="checkbox" checked={agreeInsurance} onChange={e => setAgreeInsurance(e.target.checked)} required className="peer appearance-none w-5 h-5 border-2 border-gray-300 rounded bg-white checked:bg-brand-red checked:border-brand-red transition-all outline-none focus:ring-2 focus:ring-brand-red/50" data-id="element-1262" />
                        <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1263">
                          <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1264" />
                        </svg>
                      </div>
                      <span className="text-brand-charcoal/80 text-sm leading-relaxed group-hover:text-brand-navy transition-colors" data-id="element-1265">
                        Insurance of $10 fee applies.
                      </span>
                    </label>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6" data-id="element-1266">
                    <button type="submit" className="w-full py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-1267">
                      Continue to Details
                      <ArrowRightIcon className="w-5 h-5" data-id="element-1268" />
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>;
}